/**
 * Formateo para la UI, con las convenciones uruguayas: montos con punto de
 * miles y coma decimal (1.250,50) y fechas DD/MM/AAAA.
 *
 * Antes cada página hacía `Number(item.monto).toFixed(2)`, que muestra
 * "7498.00" — formato de EE.UU., no el que usa Contaduría.
 *
 * ⚠️ El backend serializa los DECIMAL de SQL Server como STRING (json.dumps
 * con default=str, ver CLAUDE.md del backend), nunca como number. De ahí que
 * estas funciones acepten `string | number | null`.
 */

/** Montos que el backend puede devolver NULL: hay tipos de CFE sin monto
 *  total (ej. e-Resguardo trae MntTotRetenido, un e-Remito nada). */
export function formatMonto(monto: string | number | null | undefined): string {
  if (monto === null || monto === undefined || monto === "") return "—";
  const n = Number(monto);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Monto + moneda, para mostrar en una sola línea. */
export function formatMontoConMoneda(
  monto: string | number | null | undefined,
  moneda: string | null | undefined
): string {
  const importe = formatMonto(monto);
  if (importe === "—") return "—";
  return `${moneda ?? ""} ${importe}`.trim();
}

/** Fechas DATE del backend ('YYYY-MM-DD') y también ISO completas.
 *  Se parsea a mano en vez de `new Date(str)` porque `new Date('2026-07-13')`
 *  se interpreta como UTC y, en zona -03, se muestra un día antes. */
export function formatFecha(fecha: string | null | undefined): string {
  if (!fecha) return "—";
  const soloFecha = fecha.slice(0, 10);
  const [anio, mes, dia] = soloFecha.split("-");
  if (!anio || !mes || !dia) return "—";
  return `${dia}/${mes}/${anio}`;
}

/** Para timestamps (created_at, reviewed_at) donde la hora importa. */
export function formatFechaHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-UY", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/**
 * [NUEVO 20/10/2026] Para timestamps que el backend graba en UTC y serializa
 * SIN zona (la auditoría: `SYSUTCDATETIME()` + `json.dumps(default=str)` →
 * "2026-10-01 12:00:00.123000"). `formatFechaHora` los tomaría como hora local
 * y en Montevideo saldrían 3 horas adelantados. Lo usan el historial,
 * Archivadas, el autorizante de Control de pagos y "Rechazada el"
 * (`reviewed_at`, también UTC). `formatFechaHora` no se tocó: sigue sirviendo
 * para timestamps que ya traen zona.
 */
export function formatFechaHoraUtc(valor: string | null | undefined): string {
  if (!valor) return "—";
  let iso = valor.trim().replace(" ", "T");
  // JS no garantiza parsear más de 3 decimales de segundo.
  iso = iso.replace(/(\.\d{3})\d+/, "$1");
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) iso += "Z";
  return formatFechaHora(iso);
}

/**
 * Nombre legible del tipo de CFE de DGI.
 *
 * Solo se mapean los códigos CONFIRMADOS en la documentación del proyecto
 * (ver CLAUDE.md del backend). Para cualquier otro se devuelve "Tipo NNN" —
 * deliberadamente no se inventan nombres de códigos DGI no confirmados,
 * misma disciplina que el backend usa con la tabla de IndFact.
 */
const TIPOS_CFE_CONFIRMADOS: Record<number, string> = {
  111: "e-Factura",
  112: "NC e-Factura",
  113: "ND e-Factura",
  182: "e-Resguardo",
};

export function tipoCfeLabel(tipo: number | null | undefined): string {
  if (tipo === null || tipo === undefined) return "—";
  return TIPOS_CFE_CONFIRMADOS[tipo] ?? `Tipo ${tipo}`;
}

/** Serie-Número tal como lo identifica Contaduría (ej. "A-0007992"). */
export function formatNumeroCfe(serie: string | null, nro: string | null): string {
  if (!serie && !nro) return "—";
  return [serie, nro].filter(Boolean).join("-");
}

/**
 * Totales por moneda. NO se puede sumar UYU con USD — devolver un único
 * número sería un dato inventado. Se agrupa por moneda y se omiten las filas
 * sin monto/moneda.
 */
export function totalesPorMoneda(
  items: Array<{ monto: string | number | null; moneda: string | null }>
): Array<{ moneda: string; total: number }> {
  const acumulado = new Map<string, number>();
  for (const item of items) {
    if (item.monto === null || item.monto === undefined || !item.moneda) continue;
    const n = Number(item.monto);
    if (Number.isNaN(n)) continue;
    acumulado.set(item.moneda, (acumulado.get(item.moneda) ?? 0) + n);
  }
  return Array.from(acumulado.entries())
    .map(([moneda, total]) => ({ moneda, total }))
    .sort((a, b) => a.moneda.localeCompare(b.moneda));
}

/**
 * Quita acentos y pasa a minusculas, para que el buscador encuentre
 * "BARACCO" tipeando "baracco" y "Gestion" tipeando "gestion".
 *
 * Se filtra por code point en vez de con un regex de rango: el rango de
 * diacriticos combinantes escrito literal en el archivo fuente es fragil
 * (son caracteres invisibles que cualquier editor o herramienta puede
 * normalizar sin aviso). Asi el archivo queda en ASCII puro.
 */
const DIACRITICO_MIN = 0x0300; // Combining Diacritical Marks, inicio
const DIACRITICO_MAX = 0x036f; // Combining Diacritical Marks, fin

export function normalizarTexto(texto: string): string {
  let limpio = "";
  for (const caracter of texto.toLowerCase().normalize("NFD")) {
    const cp = caracter.codePointAt(0) ?? 0;
    if (cp >= DIACRITICO_MIN && cp <= DIACRITICO_MAX) continue;
    limpio += caracter;
  }
  return limpio;
}
